/**
 * FaqAccordion — accessible Q&A list.
 *
 * Uses native <details><summary> so the content is part of the DOM at first
 * paint and indexable by Google (which renders JS but still values semantic
 * HTML highest). Keyboard-navigable for free: <summary> is focusable,
 * Space/Enter toggles, no JS required.
 *
 * Pair with the parent JSON-LD FAQPage schema for rich-result eligibility.
 */
import { Plus } from "lucide-react";

export default function FaqAccordion({ items = [], heading = "Frequently asked questions" }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="card-base p-6" data-testid="faq-accordion" aria-label={heading}>
      <div className="overline mb-4">{heading}</div>
      <div className="divide-y divide-slate-200 border-y border-slate-200">
        {items.map((it, i) => (
          <details
            key={`${i}-${it.q.slice(0, 24)}`}
            className="group py-3 [&_summary::-webkit-details-marker]:hidden"
            data-testid={`faq-item-${i}`}
          >
            <summary
              className="flex items-start justify-between gap-4 cursor-pointer list-none select-none py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-kolt-orange/40 focus-visible:ring-offset-2 rounded-sm"
              data-testid={`faq-question-${i}`}
            >
              <span className="font-display font-bold text-sm sm:text-base text-slate-800 leading-snug group-open:text-kolt-slate-dark">
                {it.q}
              </span>
              <span
                className="flex-shrink-0 w-6 h-6 grid place-items-center text-slate-400 group-hover:text-kolt-orange group-open:text-kolt-orange group-open:rotate-45 transition-transform"
                aria-hidden
              >
                <Plus className="w-4 h-4" strokeWidth={2.25} />
              </span>
            </summary>
            <div
              className="mt-2 pl-1 pr-8 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
              data-testid={`faq-answer-${i}`}
            >
              {it.a}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
